import { cn } from '../cn';
import { Alerts } from '../StrokeIcons';
import StepIconSvg from '../StepIcon/StepIconSvg';
import type { StepDisplaySegment } from '../CampaignDayCard/stepDisplayInfo';
import type { TemplateBlockCardStep } from './templateStepDisplayInfo';
import { getTemplateStepDisplay } from './templateStepDisplayInfo';

export type TemplateStepRowPureProps = {
  step: TemplateBlockCardStep;
  onEdit?: (stepId: string) => void;
  className?: string;
};

/**
 * Step types whose content is OUTGOING TEXT (the actual words sent to
 * the contact). Tinted blue so the user can scan a template and tell
 * "this row is content I'm authoring" vs "this row is an automated
 * action". Mirrors the campaign-side SENT_TEXT_TYPES, minus `reminder`
 * which has no template-side analog (no per-contact action state).
 */
const SENT_TEXT_TYPES: ReadonlySet<string> = new Set([
  'message',
  'connect',
  'inmail',
  'email',
  'reminder',
]);

/**
 * Pure row inside a template delay block. Mirrors `CampaignStepRowPure`
 * but drops campaign-only concepts: no per-contact `state`, no
 * action-required green tint, no template-diff purple tint, no mark-done
 * affordance. The row's only signals are:
 *
 *   - error (red) — content is invalid (missing variable, etc.)
 *   - sent-text (blue) — outgoing message / connect / inmail / email
 *   - default (neutral) — automated action (visit / follow / like / sift)
 */
export function TemplateStepRowPure({ step, onEdit, className }: TemplateStepRowPureProps) {
  const display = getTemplateStepDisplay(step);
  const extras = display.extras;
  const siftRows = display.siftRows;
  const hasExtras = extras != null && extras.length > 0;
  const hasBody = display.body != null && display.body.length > 0;
  const hasSiftRows = siftRows != null && siftRows.length > 0;
  const hasError = step.error != null && step.error.length > 0;
  // Whether the primary line holds wrappable body content (message body,
  // connect note, subject-less inmail/email body) vs. a one-line subject or
  // short action label. Decided in getStepDisplay so both row renderers
  // agree; the separate body block below always wraps independently.
  const wrapPrimary = display.wrapPrimary ?? false;
  const isMultiLine = hasExtras || hasBody || hasSiftRows || hasError || wrapPrimary;
  // Personalized steps no longer carry static outgoing text — the body
  // is drafted per contact later. Render with the neutral "automated
  // action" tint (matches `visit` / `follow`) rather than the blue
  // sent-text tint so the row visually signals "nothing is set in stone
  // here yet".
  const isSentText = SENT_TEXT_TYPES.has(step.type) && !step.isPersonalized;

  return (
    <div
      data-testid="template-step-row"
      data-step-id={step.id}
      data-step-type={step.type}
      role="button"
      tabIndex={0}
      onClick={() => onEdit?.(step.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit?.(step.id);
        }
      }}
      className={cn(
        'group flex w-full cursor-pointer gap-3 px-3 py-2 transition-colors',
        isMultiLine ? 'items-start' : 'items-center',
        // Tint precedence: error (red) > sent-text (blue) > default
        // (neutral). Hover bg always echoes the row's tint signal so
        // tinted rows don't fall back to a generic gray hover.
        hasError
          ? 'bg-fair-pink-50 hover:bg-fair-pink-100 dark:bg-red-950/40 dark:hover:bg-red-950/60'
          : isSentText
            ? 'hover:bg-astronaut-50 dark:hover:bg-astronaut-950/40'
            : 'hover:bg-lynch-100 dark:hover:bg-slate-800',
        className,
      )}>
      <StepIconSvg
        iconType={step.type}
        className={cn(
          'size-4 shrink-0',
          hasError
            ? 'text-red-500 dark:text-red-300'
            : 'text-lynch-500 dark:text-lynch-400',
          isMultiLine && 'mt-1',
        )}
        data-testid="template-step-icon"
      />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <span
          className={cn(
            'text-base font-medium',
            hasError
              ? 'text-red-700 dark:text-red-300'
              : isSentText
                ? 'text-astronaut-800 dark:text-astronaut-200'
                : 'text-lynch-400 dark:text-lynch-500',
            wrapPrimary ? 'whitespace-pre-wrap wrap-break-word' : 'truncate',
          )}
          data-testid="template-step-primary">
          {display.primarySegments != null ? (
            <SegmentText segments={display.primarySegments} />
          ) : (
            display.primary
          )}
        </span>
        {hasExtras && extras && (
          <div className="flex flex-col" data-testid="template-step-extras">
            {extras.map((extra) => (
              <span
                key={extra.label}
                className="wrap-break-word text-xs text-lynch-700 dark:text-lynch-300"
                data-testid={`template-step-extra-${extra.label.toLowerCase()}`}>
                <span className="text-[10px] uppercase tracking-wide text-lynch-700 dark:text-lynch-300">
                  {extra.label}
                </span>{' '}
                {extra.value}
              </span>
            ))}
          </div>
        )}
        {hasBody && (
          <span
            className={cn(
              'whitespace-pre-wrap wrap-break-word text-base',
              hasError
                ? 'text-red-700 dark:text-red-300'
                : isSentText
                  ? 'text-astronaut-800 dark:text-astronaut-200'
                  : 'text-lynch-400 dark:text-lynch-500',
            )}
            data-testid="template-step-body">
            {display.bodySegments != null ? (
              <SegmentText segments={display.bodySegments} />
            ) : (
              display.body
            )}
          </span>
        )}
        {hasSiftRows && siftRows && (
          <ul className="flex flex-col gap-1" data-testid="template-step-sift-rows">
            {siftRows.map((row, idx) => (
              <li
                key={idx}
                data-testid="template-step-sift-row"
                className="flex flex-wrap items-center gap-1.5 text-sm leading-tight">
                <span
                  data-testid="template-step-sift-field"
                  className="font-semibold text-lynch-900 dark:text-lynch-100">
                  {row.field}
                </span>
                <span
                  data-testid="template-step-sift-operator"
                  className="text-lynch-500 dark:text-lynch-400">
                  {row.operator}
                </span>
                {row.value !== undefined && (
                  <span
                    data-testid="template-step-sift-value"
                    className={cn(
                      'rounded-md px-1.5 py-0.5 font-mono text-xs',
                      row.value.length === 0
                        ? 'bg-lynch-100 text-lynch-500 dark:bg-slate-800 dark:text-lynch-400'
                        : 'bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-200',
                    )}>
                    {row.value.length === 0 ? '…' : row.value}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {hasError && (
          <span
            className="mt-0.5 inline-flex items-start gap-1 wrap-break-word text-xs text-red-700 dark:text-red-300"
            data-testid="template-step-error">
            <Alerts.AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
            <span>{step.error}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function SegmentText({ segments }: { segments: StepDisplaySegment[] }) {
  return (
    <>
      {segments.map((seg, i) =>
        seg.kind === 'variable' ? (
          <span
            key={i}
            data-testid="template-step-variable"
            data-variable-id={seg.id}
            className="font-semibold text-cold-purple-800 dark:text-cold-purple-200">
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </>
  );
}
